
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function WholeHomeEnergyManagementPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Whole-Home Energy Management',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Comprehensive whole-home energy management system installation in Fresno, CA. Integrated load management, demand response, and automated energy optimization.',
    url: `${siteUrl}/services/smart-home-energy/whole-home-energy-management`,
  };

  return (
    <>
      <SEOHead
        title="Whole Home Energy Management Fresno CA | Load Management Systems"
        description="Comprehensive energy management system installation in Fresno, CA. Integrated load management, demand response, automated optimization. Reduce PG&E bills significantly."
        canonical="/services/smart-home-energy/whole-home-energy-management"
        keywords="whole home energy management Fresno, load management system, demand response, energy optimization"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Whole-Home Energy Management — Fresno, CA"
        heroSubtitle="Comprehensive energy optimization for maximum savings in Fresno's climate"
        heroImage="https://public.readdy.ai/ai/img_res/5fe56de08ab165555ec42c754a044844.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Whole%20home%20energy%20management%20dashboard%20showing%20integrated%20load%20management%20demand%20response%20and%20energy%20optimization%20with%20solar%20panels%20and%20battery%20storage%20visible%20contemporary%20home%20no%20people%20sleek%20design&width=700&height=600&seq=wholeenergy-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are taking control of their energy costs with comprehensive whole-home energy management systems that integrate monitoring, automation, and optimization. These advanced systems manage your AC, pool pump, EV charger, and other major loads to minimize consumption during expensive peak hours while maintaining comfort. Perfect for Fresno's extreme summer heat and high PG&E rates, our installations can reduce your energy bills by 30-40% through intelligent load management and demand response participation. We serve all Fresno neighborhoods with professional energy management installations that transform how you consume electricity."
        features={[
          { icon: 'ri-dashboard-line', title: 'Load Management', description: 'Optimize power distribution across your home' },
          { icon: 'ri-calendar-check-line', title: 'Demand Response', description: 'Earn credits by reducing peak usage' },
          { icon: 'ri-sun-line', title: 'Solar Integration', description: 'Maximize self-consumption of solar power' },
          { icon: 'ri-battery-charge-line', title: 'Battery Optimization', description: 'Intelligent charging and discharging' },
          { icon: 'ri-temp-hot-line', title: 'HVAC Management', description: 'Pre-cool before peak rates kick in' },
          { icon: 'ri-line-chart-line', title: 'Real-Time Optimization', description: 'Continuous adjustment for maximum savings' },
        ]}
        whatsIncluded={[
          'Complete home energy audit and assessment',
          'Energy management system installation',
          'Integration with smart thermostats and HVAC',
          'Load management for major appliances',
          'Solar and battery system integration',
          'Demand response program enrollment',
          'Automated load shifting to off-peak hours',
          'EV charger scheduling and optimization',
          'Mobile app setup with real-time monitoring',
          'Ongoing optimization and support',
        ]}
        faqs={[
          {
            question: 'How much can whole-home energy management save in Fresno?',
            answer:
              "Fresno homeowners with comprehensive energy management systems typically save 30-40% on electricity bills—that's $600-$1,200 annually for average homes. Savings come from load shifting (running AC, pool pumps, and EV chargers during off-peak hours when PG&E rates are lower), demand response credits (earning money for reducing usage during peak events), and optimized solar/battery usage. During Fresno's expensive summer months, these systems can cut cooling costs in half through intelligent pre-cooling and load management.",
          },
          {
            question: 'What is demand response and how does it work in Fresno?',
            answer:
              "Demand response programs pay you to reduce electricity usage during peak demand events (typically hot summer afternoons in Fresno when everyone's AC is running). Your energy management system automatically adjusts your AC, pool pump, and other loads during these events—usually 1-2 hours—earning you credits on your PG&E bill. Fresno homeowners can earn $100-$300 annually through demand response while barely noticing any comfort impact, since systems pre-cool your home before events start.",
          },
          {
            question: 'Can energy management work with my solar panels in Fresno?',
            answer:
              "Absolutely! Energy management systems are essential for maximizing solar value in Fresno. They shift loads like EV charging, pool pumps, and water heaters to midday when your panels produce the most power, reducing how much you buy from PG&E. For homes with battery storage, the system optimizes when to charge batteries (during peak solar production) and when to discharge them (during expensive evening hours). This is especially valuable under PG&E's NEM 3.0 rules where solar export rates are much lower than import rates.",
          },
          {
            question: 'Will I notice any difference with energy management in my Fresno home?',
            answer:
              "Most Fresno homeowners notice no difference in comfort or convenience—that's the beauty of automated energy management. Your AC pre-cools your home before peak rates, so you stay comfortable while saving money. Your pool pump runs during off-peak hours when you're not swimming anyway. Your EV charges overnight when rates are lowest. The system works invisibly in the background, and you just see lower PG&E bills. You can always override automation if needed for special circumstances.",
          },
          {
            question: 'What equipment is needed for whole-home energy management in Fresno?',
            answer:
              "A comprehensive system includes an energy management controller (the brain), energy monitoring at your panel, smart thermostats for AC control, smart switches or relays for major loads (pool pump, water heater), and optionally smart EV chargers. For solar homes, integration with your inverter and battery system. We design systems based on your specific needs and budget—you can start with basic load management and add features like demand response or battery optimization later.",
          },
          {
            question: 'How long does energy management installation take in a Fresno home?',
            answer:
              "Basic energy management installation (monitoring, smart thermostat, and load control for 2-3 major appliances) takes 1 full day. Comprehensive systems with solar/battery integration, demand response enrollment, and control of 5-6 major loads take 2-3 days for a typical Fresno home. We handle all electrical work, device installation, network setup, and system programming. You'll start seeing savings immediately, and the system continues optimizing as it learns your usage patterns.",
          },
        ]}
        ctaText="Optimize Your Home's Energy"
      />
    </>
  );
}
